<template>
  <woot-modal :show="show" :on-close="handleClose">
    <div class="w-full mx-auto">
      <form
        class="flex flex-col gap-5 max-h-[60vh] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900"
        @submit.prevent="onSubmit"
      >
        <div class="px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {{ $t('BUSINESS_DATA.CATEGORY.EDIT_TITLE') }}
          </h2>
        </div>

        <div class="flex-1 overflow-y-auto space-y-4 px-5 pb-5">
          <div class="space-y-2">
            <FormLabel
              :label="$t('BUSINESS_DATA.CATEGORY.TYPE')"
              :required="true"
              :has-error="v$.form.category_type.$error"
            />
            <select
              v-model="form.category_type"
              required
              :class="[
                'w-full rounded-lg border bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 dark:bg-slate-800 dark:text-slate-100',
                v$.form.category_type.$error
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20 dark:border-red-400 dark:focus:border-red-400'
                  : 'border-slate-200 focus:border-hichat-500 focus:ring-hichat-500/20 dark:border-slate-600'
              ]"
              @blur="v$.form.category_type.$touch"
              @change="v$.form.category_type.$touch"
            >
              <option value="">{{ $t('BUSINESS_DATA.CATEGORY.SELECT_TYPE') }}</option>
              <option value="faq">{{ $t('BUSINESS_DATA.CATEGORY.FAQ') }}</option>
              <option value="data">{{ $t('BUSINESS_DATA.CATEGORY.DATA') }}</option>
              <option value="image">{{ $t('BUSINESS_DATA.CATEGORY.IMAGE') }}</option>
            </select>
            <div v-if="v$.form.category_type.$error" class="text-xs text-red-600 dark:text-red-400">
              {{ $t('BUSINESS_DATA.CATEGORY.TYPE_ERROR') }}
            </div>
          </div>

          <div class="space-y-2">
            <woot-input
              v-model="form.name"
              :label="$t('BUSINESS_DATA.CATEGORY.NAME')"
              :required="true"
              :placeholder="$t('BUSINESS_DATA.CATEGORY.NAME_PLACEHOLDER')"
              :error="v$.form.name.$error ? $t('BUSINESS_DATA.CATEGORY.NAME_ERROR') : ''"
              :class="{ error: v$.form.name.$error }"
              maxlength="100"
              @blur="v$.form.name.$touch"
            />
          </div>

          <div class="space-y-2">
            <FormLabel
              :label="$t('BUSINESS_DATA.CATEGORY.COLOR')"
              :required="true"
              :has-error="false"
            />
            <div class="flex gap-2">
              <input
                v-model="form.color"
                type="color"
                class="h-10 w-12 rounded-lg border border-slate-200 bg-white p-0 dark:border-slate-600 dark:bg-slate-800"
              />
              <input
                v-model="form.color"
                :placeholder="$t('BUSINESS_DATA.CATEGORY.COLOR_PLACEHOLDER')"
                required
                pattern="^#[0-9A-Fa-f]{6}$"
                class="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-hichat-500 focus:outline-none focus:ring-2 focus:ring-hichat-500/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
              />
            </div>
          </div>

          <div class="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/60">
            <span
              class="inline-block rounded-full px-3 py-1 text-sm font-medium"
              :style="{ backgroundColor: form.color, color: getTextColor(form.color) }"
            >
              {{ form.name || $t('BUSINESS_DATA.CATEGORY.PREVIEW_PLACEHOLDER') }}
            </span>
          </div>
        </div>

        <div class="flex justify-end gap-3 border-t border-slate-200 px-5 py-4 dark:border-slate-700">
          <woot-button type="button" variant="clear" @click="handleClose">
            {{ $t('COMMON.CANCEL') }}
          </woot-button>
          <woot-button type="submit" color-scheme="primary" :loading="isSaving" :disabled="!isFormValid">
            {{ $t('COMMON.UPDATE') }}
          </woot-button>
        </div>
      </form>
    </div>
  </woot-modal>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import { required, maxLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import FormLabel from 'dashboard/components/widgets/forms/FormLabel.vue';

export default {
  name: 'CategoryEditModal',
  components: { FormLabel },
  setup() {
    return { v$: useVuelidate() };
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    category: {
      type: Object,
      default: null,
    },
    categoryType: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isSaving: false,
      form: {
        name: '',
        color: '#6B7280',
        category_type: '',
      },
    };
  },
  computed: {
    isFormValid() {
      return (
        this.form.name.trim() &&
        this.form.color &&
        this.form.category_type &&
        this.form.name.length <= 100
      );
    },
    currentAccountId() {
      return this.$store.getters.getCurrentAccountId;
    },
  },
  validations: {
    form: {
      name: { required, maxLength: maxLength(100) },
      category_type: { required },
      color: { required },
    },
  },
  watch: {
    category: {
      immediate: true,
      handler(category) {
        if (category) {
          this.form = {
            name: category.name || '',
            color: category.color || '#6B7280',
            category_type: category.category_type || this.categoryType || '',
          };
        }
      },
    },
  },
  methods: {
    getTextColor(color) {
      if (!color) return '#000000';
      const hex = color.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.5 ? '#000000' : '#FFFFFF';
    },
    handleClose() {
      this.v$.$reset();
      this.$emit('close');
    },
    async onSubmit() {
      this.v$.$touch();
      if (this.v$.$invalid || !this.isFormValid || !this.category) {
        return;
      }
      this.isSaving = true;
      try {
        await window.axios.put(
          `/api/v1/accounts/${this.currentAccountId}/business_categories/${this.category.id}`,
          {
            name: this.form.name.trim(),
            color: this.form.color,
            category_type: this.form.category_type,
          }
        );
        this.$emit('updated', { id: this.category.id });
      } catch (error) {
        const message =
          error?.response?.data?.error ||
          (Array.isArray(error?.response?.data?.errors) ? error.response.data.errors.join(', ') : null) ||
          error?.response?.data?.message ||
          this.$t('BUSINESS_DATA.ERROR.UPDATE_FAILED');
        useAlert(message, { type: 'error' });
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>
